import React, { createContext, useContext, useMemo } from 'react';
import type { UserFocusItem } from '@shared/types';

/**
 * Read-only view of today's focus items for surfaces outside the daily
 * practice that need to know what the practice is already asking for.
 *
 * There is exactly ONE subscribeToUserFocus listener in the app — the one in
 * AuthenticatedApp's useFocusItems. This context re-publishes that hook's
 * result so other surfaces can read it without opening a second snapshot.
 * Do not subscribe again; provide from useFocusItems and consume here.
 */
export interface FocusItemsContextValue {
    /** Today's visible focus items (active + completed/dismissed today). */
    focusItems: UserFocusItem[];
    /** True until the focus subscription has answered for this user/day. */
    focusLoading: boolean;
}

/**
 * Default = "no focus items, and nothing is pending". Surfaces rendered
 * outside AuthenticatedApp (demo mode, isolated unit tests) therefore behave
 * exactly as they did before this context existed.
 */
const FocusItemsContext = createContext<FocusItemsContextValue>({
    focusItems: [],
    focusLoading: false,
});

interface FocusItemsProviderProps extends FocusItemsContextValue {
    children: React.ReactNode;
}

export const FocusItemsProvider: React.FC<FocusItemsProviderProps> = ({
    focusItems,
    focusLoading,
    children,
}) => {
    const value = useMemo(() => ({ focusItems, focusLoading }), [focusItems, focusLoading]);
    return <FocusItemsContext.Provider value={value}>{children}</FocusItemsContext.Provider>;
};

export const useFocusItemsContext = (): FocusItemsContextValue => useContext(FocusItemsContext);
